import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import AppDownloadButton from '../components/AppDownloadButton';
import { User, Storefront, Buildings, Briefcase, Star } from '@phosphor-icons/react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import BrandLogo from '../components/BrandLogo';
import CityLocationInput from '../components/CityLocationInput';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Registration roles (5 visual cards). Each card maps to a backend `role`
 * (customer / vendor / professional) plus an optional locked `category`.
 *
 * For "Vendor" and "Professional" the user picks a sub-category below.
 * For "Venue Provider" the category is locked to `venue`.
 * "Entertainment Professional" is a UX-only split of `professional` —
 * the backend role is the same but we curate a different category list.
 */
const ROLES = [
  {
    key: 'customer',
    title: 'Customer',
    desc: 'I want to book vendors & services',
    icon: User,
    role: 'customer',
    category: null,
  },
  {
    key: 'vendor',
    title: 'Vendor',
    desc: 'Photographer, caterer, decorator, music, flowers, return gifts',
    icon: Storefront,
    role: 'vendor',
    category: null,
    earnings: { min: 40000, max: 1000000, unit: 'event' },
  },
  {
    key: 'venue_provider',
    title: 'Venue Provider',
    desc: 'I rent out venues for events',
    icon: Buildings,
    role: 'vendor',
    category: 'venue',
    earnings: { min: 100000, max: 5000000, unit: 'event' },
  },
  {
    key: 'professional',
    title: 'Professional',
    desc: 'Chef, priest, photographer, makeup artist',
    icon: Briefcase,
    role: 'professional',
    category: null,
    earnings: { min: 4000, max: 100000, unit: 'event' },
  },
  {
    key: 'entertainment_professional',
    title: 'Entertainment Pro',
    desc: 'Magician, DJ, dancer, anchor, costume artist',
    icon: Star,
    role: 'professional',
    category: null,
    earnings: { min: 4000, max: 100000, unit: 'event' },
  },
];

/**
 * Format an earnings band for display.
 * 40000 → "₹40K", 100000 → "₹1L", 5000000 → "₹50L", 10000000 → "₹1Cr"
 */
const fmtINR = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(n % 10000000 === 0 ? 0 : 1).replace(/\.0$/, '')}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1).replace(/\.0$/, '')}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n}`;
};

const VENDOR_CATEGORIES = [
  { value: 'photographer', label: 'Photographer' },
  { value: 'caterer', label: 'Caterer' },
  { value: 'decorator', label: 'Decorator' },
  { value: 'birthday_decorator', label: 'Birthday Decorator' },
  { value: 'nathaswaram_music', label: 'Celebrate With Music' },
  { value: 'return_gift', label: 'Return Gift / Muhurtham Pai' },
  { value: 'flowers_pooja', label: 'Flowers & Pooja Items' },
  { value: 'cake_vendor', label: 'Cake Vendor / Bakery' },
];

// Professional sub-categories grouped exactly the same way customers see
// them on the Instant Pricing page.
const PROFESSIONAL_GROUPS = [
  {
    id: 'catering_staff',
    label: 'Catering Staff',
    items: [
      { value: 'chef', label: 'Chef' },
      { value: 'biryani_specialist', label: 'Biryani Specialist' },
      { value: 'assistant_chef', label: 'Assistant Chef' },
      { value: 'server_waiter', label: 'Server / Waiter' },
      { value: 'catering_team', label: 'Catering Team' },
    ],
  },
  {
    id: 'ceremony_pros',
    label: 'Ceremony Pros',
    items: [
      { value: 'priest', label: 'Priest / Purohit' },
    ],
  },
  {
    id: 'media_pros',
    label: 'Media Pros',
    items: [
      { value: 'photographer', label: 'Photographer' },
      { value: 'videographer', label: 'Videographer' },
    ],
  },
  {
    id: 'beauty_decor',
    label: 'Beauty & Decor Pros',
    items: [
      { value: 'makeup_artist', label: 'Makeup Artist' },
      { value: 'mehndi_artist', label: 'Mehndi Artist' },
      { value: 'choreographer', label: 'Choreographer' },
      { value: 'decoration_team', label: 'Decoration Team' },
    ],
  },
];

const ENTERTAINMENT_GROUP = {
  id: 'entertainment',
  label: 'Entertainment Professionals',
  items: [
    { value: 'magician', label: 'Magician' },
    { value: 'standup_comedian', label: 'Standup Comedian' },
    { value: 'anchor_emcee', label: 'Anchor / Emcee' },
    { value: 'dancer', label: 'Dancer / Dance Troupe' },
    { value: 'costume_performer', label: 'Costume Performer' },
    { value: 'buffoon', label: 'Buffoon / Clown' },
    { value: 'singer', label: 'Singer / Vocalist' },
    { value: 'singer_karaoke', label: 'Singer with Karaoke' },
    { value: 'mimicry_artist', label: 'Mimicry Artist' },
    { value: 'musician', label: 'Musician / Instrument Player' },
    { value: 'dj', label: 'DJ' },
  ],
};

// Priest title taxonomy (Feb 2026) — single title per priest, grouped by wedding tradition.
// Whatever the priest picks here shows as a prefix before their name in customer listings,
// and they're only listed in marriage / engagement wedding flows matching their tradition.
const PRIEST_TITLE_GROUPS = [
  { tradition: 'hindu', label: 'Hindu Wedding', sub: 'Hindu Priest',
    titles: ['Iyer', 'Pandit', 'Purohit', 'Gurukkal', 'Sastri', 'Swami'] },
  { tradition: 'christian', label: 'Christian Wedding', sub: 'Christian Officiant',
    titles: ['Priest', 'Father', 'Pastor', 'Reverend', 'Minister'] },
  { tradition: 'muslim', label: 'Muslim Wedding', sub: 'Nikah Officiant',
    titles: ['Imam', 'Qazi', 'Maulana', 'Mufti', 'Sheikh'] },
];

/**
 * Build a flat index across every role + category. The Register page
 * now exposes this list as a single native &lt;select&gt; dropdown so the
 * user can pick their profession directly — selecting any item updates
 * BOTH `roleKey` and `formData.category`.
 */
const PROFESSION_OPTIONS = (() => {
  const groups = [];
  // Customer pinned to the very TOP — most signups are customers.
  groups.push({
    label: 'Just looking to book',
    items: [{ roleKey: 'customer', label: 'I just want to book vendors (Customer)', category: '' }],
  });
  // Vendors (registered businesses)
  groups.push({
    label: 'Vendors (registered business)',
    items: VENDOR_CATEGORIES.map((v) => ({ roleKey: 'vendor', label: v.label, category: v.value })),
  });
  // Venue Provider
  groups.push({
    label: 'Venue Provider',
    items: [{ roleKey: 'venue_provider', label: 'Venue / Hall / Mandapam', category: 'venue' }],
  });
  // Professional sub-groups
  for (const g of PROFESSIONAL_GROUPS) {
    groups.push({
      label: `Professional — ${g.label}`,
      items: g.items.map((it) => ({ roleKey: 'professional', label: it.label, category: it.value })),
    });
  }
  // Entertainment professionals
  groups.push({
    label: 'Entertainment Pros',
    items: ENTERTAINMENT_GROUP.items.map((it) => ({ roleKey: 'entertainment_professional', label: it.label, category: it.value })),
  });
  return groups;
})();

// Flat lookup so we can show the selected label inside the confirmation badge.
const PROFESSION_LABEL_LOOKUP = (() => {
  const map = {};
  for (const g of PROFESSION_OPTIONS) {
    for (const it of g.items) {
      map[`${it.roleKey}:${it.category}`] = it.label;
    }
  }
  return map;
})();

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || '';
  const { user, checkAuth } = useAuth();
  const { t, lang, setLang } = useLanguage();

  // On first visit to Create Account, default the language to Tamil
  // (most of our registering vendors / pros are from Tamil Nadu). We only
  // do this when the user hasn't *manually* picked a language before — the
  // language switcher in the header always wins over this default.
  useEffect(() => {
    const userPicked = localStorage.getItem('eventbook_lang_user_picked') === '1';
    if (!userPicked && lang !== 'ta') {
      setLang('ta');
    }
  }, []); // eslint-disable-line

  // Two-stage UI: dropdown placeholder by default — user MUST actively
  // pick a role. Only the referral-link flow pre-selects 'professional'.
  const initialRoleKey = referralCode ? 'professional' : '';
  const [roleKey, setRoleKey] = useState(initialRoleKey);

  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [confirmedAge, setConfirmedAge] = useState(false);
  // OTP gate
  const [otpStep, setOtpStep] = useState('idle');
  const [otpCode, setOtpCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  const selectedRole = useMemo(
    () => ROLES.find(r => r.key === roleKey) || ROLES[0],
    [roleKey],
  );

  const [formData, setFormData] = useState({
    phone: '',
    name: '',
    business_name: '',
    category: selectedRole.category || '',
    location: '',
    description: '',
  });

  // Single-title priest taxonomy (Feb 2026) — exactly one title from exactly one tradition.
  const [priestTitle, setPriestTitle] = useState({ tradition: '', title: '' });
  // Keep legacy state name alias to avoid touching unrelated logic; treat it as
  // an array of one item for the validation check below.
  const priestTraditions = priestTitle.tradition && priestTitle.title ? [`${priestTitle.tradition}:${priestTitle.title}`] : [];

  // Catering team staff declaration (shown only when category=catering_team)
  const [cateringStaff, setCateringStaff] = useState({
    chef: 1,
    assistant_chef: 1,
    server: 1,
    cleanup: 1,
  });

  // Filtered search results removed — see PROFESSION_OPTIONS native select below.

  React.useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  React.useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  // ─── Gov-checkbox ↔ category auto-flip ───────────────────────────────
  // Caterer ↔ Catering Team, Decorator ↔ Decoration Team share a label
  // space but live in different role buckets. Ticking "registered under
  // government" promotes the pro-side category to its vendor twin (and
  // un-ticking demotes it back). We only react to the checkbox toggle
  // itself — picking explicitly from the dropdown is never auto-rewritten.
  React.useEffect(() => {
    const cat = formData.category;
    const reg = formData.business_registered === true;
    const VENDOR_TWIN = { catering_team: 'caterer', decoration_team: 'decorator' };
    const PRO_TWIN    = { caterer: 'catering_team', decorator: 'decoration_team' };
    if (reg && VENDOR_TWIN[cat]) {
      setFormData((f) => ({ ...f, category: VENDOR_TWIN[cat] }));
      setRoleKey('vendor');
    } else if (!reg && PRO_TWIN[cat]) {
      setFormData((f) => ({ ...f, category: PRO_TWIN[cat] }));
      setRoleKey('professional');
    }
  }, [formData.business_registered]); // eslint-disable-line react-hooks/exhaustive-deps

  // Note: pickProfession() now sets roleKey AND formData.category in one
  // shot, so we no longer need a "reset category when role changes"
  // effect — that effect was for the legacy role-cards UI.

  const pickProfession = (roleKeyVal, categoryVal) => {
    setRoleKey(roleKeyVal);
    setFormData((f) => ({ ...f, category: categoryVal || '' }));
    const label = PROFESSION_LABEL_LOOKUP[`${roleKeyVal}:${categoryVal}`];
    if (label) toast.success(`Selected: ${label}`);
  };

  const selectedOptionValue = `${roleKey}:${formData.category || ''}`;
  const selectedProfessionLabel = PROFESSION_LABEL_LOOKUP[selectedOptionValue];

  const sendOtp = async () => {
    if (!formData.phone || formData.phone.length < 8) {
      toast.error('Enter a valid phone number first');
      return;
    }
    setSendingOtp(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/otp/send`, {
        phone: formData.phone,
        purpose: 'registration',
      });
      setOtpStep('sent');
      setResendIn(30);
      if (data?.otp_for_testing) {
        toast.success(`OTP sent (dev mode): ${data.otp_for_testing}`, { duration: 8000 });
      } else {
        toast.success('OTP sent to your phone via SMS');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async (codeOverride) => {
    const code = (codeOverride ?? otpCode).replace(/\D/g, '');
    if (code.length < 4) {
      toast.error('Enter the OTP sent to your phone');
      return;
    }
    setVerifyingOtp(true);
    try {
      await axios.post(`${BACKEND_URL}/api/otp/verify`, {
        phone: formData.phone,
        otp: code,
      });
      setOtpStep('verified');
      toast.success('Phone verified!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!roleKey) {
      toast.error('Please pick what describes you from the dropdown above');
      return;
    }
    if (!formData.phone || !formData.name) {
      toast.error('Please fill all required fields');
      return;
    }
    if (selectedRole.role !== 'customer' && !formData.category) {
      toast.error('Please select a category');
      return;
    }
    if (formData.category === 'priest' && (!priestTitle.tradition || !priestTitle.title)) {
      toast.error('Please pick the wedding tradition you can perform and your title');
      return;
    }
    if (formData.category === 'catering_team') {
      const c = cateringStaff;
      if (c.chef < 1 || c.assistant_chef < 1 || c.server < 1 || c.cleanup < 1) {
        toast.error('Catering teams must have at least 1 chef, 1 assistant chef, 1 server, and 1 cleanup');
        return;
      }
    }
    // Vendors, venue providers, and professionals must enter their location
    // so customers can find them in city-scoped searches.
    if (selectedRole.role !== 'customer' && !formData.location.trim()) {
      toast.error('Please enter your city / current location');
      return;
    }
    if (!confirmedAge) {
      toast.error('Please confirm you are 18 years or older');
      return;
    }
    if (!agreedToTerms) {
      toast.error('Please accept the Terms of Service and Privacy Policy');
      return;
    }
    if (otpStep !== 'verified') {
      toast.error('Please verify your phone with OTP first');
      return;
    }
    // Vendor role requires the govt-registration tick — otherwise we steer
    // them to the Professional role (which doesn't need any business papers).
    if (selectedRole.role === 'vendor' && selectedRole.key !== 'venue_provider' &&
        formData.business_registered !== true) {
      toast.error('Please confirm your business is govt-registered, or switch to Professional.');
      return;
    }

    setLoading(true);
    try {
      const isPaidRole = selectedRole.role === 'vendor' || selectedRole.role === 'professional';
      const payload = {
        phone: formData.phone,
        name: formData.name,
        role: selectedRole.role,
        ...(isPaidRole ? {
          business_name: formData.business_name,
          category: formData.category,
          location: formData.location,
          description: formData.description,
        } : {}),
        ...(selectedRole.role === 'vendor' && selectedRole.key !== 'venue_provider' ? {
          business_registered: true,
        } : {}),
        ...(isPaidRole && referralCode ? { referral_code: referralCode } : {}),
        ...(formData.category === 'priest' && priestTitle.tradition && priestTitle.title ? {
          priest_tradition: priestTitle.tradition,
          priest_title: priestTitle.title,
        } : {}),
        ...(formData.category === 'catering_team' ? { catering_team_size: cateringStaff } : {}),
      };

      await axios.post(`${BACKEND_URL}/api/auth/register-phone`, payload, { withCredentials: true });

      try {
        await axios.post(`${BACKEND_URL}/api/me/consent`, {
          terms_version: '1.0',
          privacy_version: '1.0',
          refund_version: '1.0',
          age_confirmed: true,
        }, { withCredentials: true });
      } catch { /* best-effort */ }

      toast.success('Registration successful!');
      await checkAuth();
      navigate('/dashboard');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  // Build the role-dependent extras shown AFTER the user picks their
  // profession in the dropdown above. The dropdown is the sole category
  // picker — this block only renders things the dropdown can't handle:
  //   • Venue lock-info note
  //   • Gov-registration checkbox (vendor roles only)
  const renderCategoryPicker = () => {
    if (selectedRole.role === 'customer') return null;
    if (selectedRole.key === 'venue_provider') {
      return (
        <div className="p-3 bg-brand-accent/5 border border-brand-primary/20 rounded text-sm" data-testid="venue-locked-info">
          <span className="font-bold">Category:</span> Venue Provider (locked) — you'll add specific venue types (Mandapam, Banquet, Garden, etc.) inside your dashboard after registration.
        </div>
      );
    }
    if (selectedRole.key === 'vendor') {
      const registered = formData.business_registered === true;
      return (
        <div className="border-2 border-[#E8E1D5] rounded p-3 space-y-2"
          data-testid="vendor-registered-check-block">
          <label className="flex items-start gap-2 cursor-pointer" data-testid="vendor-registered-checkbox-label">
            <input
              type="checkbox"
              checked={registered}
              onChange={(e) => setFormData({
                ...formData,
                business_registered: e.target.checked,
              })}
              className="mt-0.5 cursor-pointer w-4 h-4"
              data-testid="vendor-registered-checkbox"
            />
            <span className="text-sm">
              My shop / business is <strong>registered under government</strong>
            </span>
          </label>
          {!registered && (
            <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-[12px] text-orange-800"
              data-testid="vendor-not-registered-hint">
              If your work isn&apos;t a registered business, please pick the matching <strong>Professional</strong> option from the dropdown above — same earning, faster KYC, no govt papers needed.
            </div>
          )}
        </div>
      );
    }
    // Professional or Entertainment Professional — nothing extra needed
    // here; the dropdown already captured the exact category.
    return null;
  };

  return (
    <div className="min-h-screen bg-sand-50 flex items-start sm:items-center justify-center p-4 sm:p-8 pb-44 sm:pb-8">
      <div className="w-full max-w-3xl">
        <div className="text-center mb-8">
          <div className="inline-flex justify-center"><BrandLogo size="xl" linkTo="/" /></div>
          <div className="mt-3 flex justify-center"><LanguageSwitcher /></div>
          <div className="mt-4"><AppDownloadButton /></div>
        </div>

        <Card className="bg-white border-[#E8E1D5]">
          <CardHeader>
            <CardTitle className="font-heading text-3xl">{t('auth.create_account')}</CardTitle>
            <CardDescription>{t('auth.join_eventbook')}</CardDescription>
          </CardHeader>
          <CardContent>
            {referralCode && (
              <div className="mb-4 p-3 bg-green-50 border border-green-300 rounded text-sm text-green-800" data-testid="referral-banner">
                You've been invited! Register as a <span className="font-bold">Vendor</span>, <span className="font-bold">Venue Provider</span>, <span className="font-bold">Professional</span>, or <span className="font-bold">Entertainment Pro</span> to activate the referral bonus for both of you.
                <span className="block text-xs mt-1 text-green-600">Invite Code: <span className="font-bold" data-testid="referral-banner-code">{referralCode}</span></span>
                {selectedRole.role === 'customer' && (
                  <span className="block text-xs mt-1 text-amber-700 font-medium" data-testid="referral-customer-warning">
                    Heads up: selecting "Customer" will not apply this invite — pick a paid role above.
                  </span>
                )}
              </div>
            )}

            {/* Profession picker — single native dropdown listing every
                role + category. Selecting any item updates roleKey AND
                formData.category and shows a clear confirmation badge. */}
            <div className="mb-5">
              <Label htmlFor="profession-select" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 block">
                I am a… *
              </Label>
              <select
                id="profession-select"
                value={selectedOptionValue}
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  const [rk, ...rest] = v.split(':');
                  const cat = rest.join(':');
                  pickProfession(rk, cat);
                }}
                className="w-full h-11 px-3 border border-[#E8E1D5] bg-white text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-brand-primary"
                data-testid="profession-select"
              >
                <option value="">— Choose what describes you —</option>
                {PROFESSION_OPTIONS.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.items.map((it) => (
                      <option
                        key={`${it.roleKey}:${it.category}`}
                        value={`${it.roleKey}:${it.category}`}
                      >
                        {it.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {selectedProfessionLabel && (
                <div
                  className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-300 rounded text-sm text-green-800"
                  data-testid="profession-selected-confirm"
                >
                  <span className="text-green-600 font-bold">✓</span>
                  <span>
                    Selected: <strong>{selectedProfessionLabel}</strong>
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-green-700">
                      ({selectedRole.title})
                    </span>
                  </span>
                </div>
              )}
            </div>

            {/* The previous "role cards" + "search profession" blocks
                have been replaced by the single &lt;select&gt; above. */}

            <form onSubmit={handleRegister} className="space-y-5">
              {/* Category picker (role-dependent) */}
              {renderCategoryPicker()}

              {/* Priest title — exactly one title from one wedding tradition. */}
              {formData.category === 'priest' && (
                <div className="border-2 border-brand-primary/40 rounded p-3 bg-brand-accent/5" data-testid="priest-traditions-block">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-1 block">
                    Which wedding can you officiate? *
                  </Label>
                  <p className="text-xs text-text-secondary mb-3">
                    Pick the wedding tradition you perform and your title. Your title will be shown before your name in customer listings.
                  </p>
                  <div className="space-y-3">
                    {PRIEST_TITLE_GROUPS.map((g) => (
                      <div key={g.tradition} className="border-2 border-[#E8E1D5] rounded p-3 bg-white"
                        data-testid={`priest-group-${g.tradition}`}>
                        <div className="text-sm font-bold text-text-primary mb-1">{g.label}</div>
                        <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-2">{g.sub}</div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {g.titles.map((t) => {
                            const checked = priestTitle.tradition === g.tradition && priestTitle.title === t;
                            return (
                              <label
                                key={t}
                                className={`flex items-center gap-2 p-2 border-2 rounded cursor-pointer text-sm transition-all ${checked ? 'border-brand-primary bg-brand-accent/10' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`}
                                data-testid={`priest-title-${g.tradition}-${t.toLowerCase()}`}
                              >
                                <input
                                  type="radio"
                                  name="priest_title"
                                  checked={checked}
                                  onChange={() => setPriestTitle({ tradition: g.tradition, title: t })}
                                  className="h-4 w-4 accent-brand-primary flex-shrink-0"
                                />
                                <span>{t}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  {priestTitle.tradition && priestTitle.title && (
                    <p className="text-xs text-green-700 mt-2 font-medium" data-testid="priest-traditions-count">
                      Selected: <strong>{priestTitle.title}</strong> ({priestTitle.tradition === 'hindu' ? 'Hindu Wedding' : priestTitle.tradition === 'christian' ? 'Christian Wedding' : 'Muslim Wedding'})
                    </p>
                  )}
                </div>
              )}

              {/* Catering team staff declaration — required when category=catering_team */}
              {formData.category === 'catering_team' && (
                <div className="border-2 border-brand-primary/40 rounded p-3 bg-brand-accent/5" data-testid="catering-staff-block">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-1 block">
                    Declare your team size *
                  </Label>
                  <p className="text-xs text-text-secondary mb-3">
                    Customers will see your team strength when booking. Minimum 1 per role is required.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { key: 'chef', label: 'Chefs' },
                      { key: 'assistant_chef', label: 'Asst Chefs' },
                      { key: 'server', label: 'Servers' },
                      { key: 'cleanup', label: 'Cleanup' },
                    ].map(({ key, label }) => (
                      <div key={key} className="space-y-1">
                        <Label className="text-xs font-bold text-text-secondary">{label} *</Label>
                        <Input
                          type="number"
                          min="1"
                          value={cateringStaff[key]}
                          onChange={(e) => {
                            const v = Math.max(0, parseInt(e.target.value || '0', 10));
                            setCateringStaff(s => ({ ...s, [key]: v }));
                          }}
                          className={`rounded-none h-9 ${cateringStaff[key] < 1 ? 'border-red-400 bg-red-50/30' : 'border-[#E8E1D5]'}`}
                          data-testid={`catering-staff-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-text-secondary mt-2 italic">
                    Total team size: {Object.values(cateringStaff).reduce((a, b) => a + (b || 0), 0)} member{Object.values(cateringStaff).reduce((a, b) => a + (b || 0), 0) === 1 ? '' : 's'}
                  </p>
                </div>
              )}

              {/* Phone Number */}
              <div>
                <Label htmlFor="phone" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+919876543210"
                  value={formData.phone}
                  onChange={(e) => {
                    setFormData({ ...formData, phone: e.target.value });
                    if (otpStep !== 'idle') { setOtpStep('idle'); setOtpCode(''); }
                  }}
                  required
                  className="rounded-none border-[#E8E1D5] focus:ring-brand-primary"
                  data-testid="register-phone-input"
                />
              </div>

              {/* OTP gate */}
              <div className="border-t border-[#E8E1D5] pt-4">
                {otpStep === 'idle' && (
                  <Button
                    type="button"
                    onClick={sendOtp}
                    disabled={sendingOtp || !formData.phone}
                    className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none disabled:opacity-50"
                    data-testid="send-otp-btn"
                  >
                    {sendingOtp ? 'Sending...' : 'Send OTP to Phone'}
                  </Button>
                )}
                {otpStep === 'sent' && (
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Enter 6-Digit OTP *</Label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={otpCode}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setOtpCode(v);
                          // Auto-verify the instant the 6th digit lands so the
                          // user doesn't have to reach for the button. We
                          // pass `v` directly because React hasn't committed
                          // setOtpCode(v) by the time verifyOtp() runs —
                          // reading otpCode from closure here is stale.
                          if (v.length === 6) verifyOtp(v);
                        }}
                        placeholder="123456"
                        className="rounded-none border-[#E8E1D5] font-mono tracking-widest text-lg"
                        data-testid="otp-input"
                      />
                      <Button
                        type="button"
                        onClick={verifyOtp}
                        disabled={verifyingOtp || otpCode.length < 4}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-none disabled:opacity-50"
                        data-testid="verify-otp-btn"
                      >
                        {verifyingOtp ? '...' : 'Verify'}
                      </Button>
                    </div>
                    <div className="flex items-center justify-between text-xs text-text-secondary">
                      <span>OTP sent to {formData.phone}</span>
                      {resendIn > 0 ? (
                        <span data-testid="otp-resend-cooldown">Resend in {resendIn}s</span>
                      ) : (
                        <button
                          type="button"
                          onClick={sendOtp}
                          disabled={sendingOtp}
                          className="text-brand-primary hover:underline font-bold"
                          data-testid="otp-resend-btn"
                        >
                          Resend OTP
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {otpStep === 'verified' && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-300 rounded text-green-800 text-sm" data-testid="otp-verified-badge">
                    <span className="text-green-600 font-bold">✓</span>
                    Phone verified — {formData.phone}
                  </div>
                )}
              </div>

              {/* Name */}
              <div>
                <Label htmlFor="name" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-none border-[#E8E1D5] focus:ring-brand-primary"
                  data-testid="register-name-input"
                />
              </div>

              {/* Paid-role extra fields */}
              {(selectedRole.role === 'vendor' || selectedRole.role === 'professional') && (
                <>
                  {/* Business name is only meaningful for vendors / venue providers.
                      Professionals and entertainment pros operate under their personal
                      name — hide the field for them. */}
                  {selectedRole.role === 'vendor' && (
                    <div>
                      <Label htmlFor="business_name" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                        {selectedRole.key === 'venue_provider' ? 'Venue / Business Name' : 'Business Name'}
                      </Label>
                      <Input
                        id="business_name"
                        type="text"
                        value={formData.business_name}
                        onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                        className="rounded-none border-[#E8E1D5] focus:ring-brand-primary"
                        data-testid="business-name-input"
                      />
                    </div>
                  )}

                  <CityLocationInput
                    value={formData.location}
                    onChange={(v) => setFormData({ ...formData, location: v })}
                    label="City / Current Location"
                    required
                    testId="location"
                  />

                  <div>
                    <Label htmlFor="description" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                      {selectedRole.role === 'professional' ? 'About You' : 'Description'}
                    </Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      placeholder={selectedRole.role === 'professional' ? 'Describe your experience and services...' : ''}
                      className="rounded-none border-[#E8E1D5] focus:ring-brand-primary"
                      data-testid="description-input"
                    />
                  </div>
                </>
              )}

              <div className="border-t border-[#E8E1D5] pt-4 space-y-3" data-testid="consent-section">
                <label className="flex items-start gap-2 text-sm text-text-primary cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmedAge}
                    onChange={(e) => setConfirmedAge(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-brand-primary"
                    data-testid="age-confirm-checkbox"
                  />
                  <span>I confirm I am <strong>18 years of age or older</strong>.</span>
                </label>
                <label className="flex items-start gap-2 text-sm text-text-primary cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-brand-primary"
                    data-testid="terms-agree-checkbox"
                  />
                  <span>
                    I agree to BeeWeddy's{' '}
                    <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-brand-primary underline" data-testid="terms-link">
                      Terms of Service
                    </Link>
                    ,{' '}
                    <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-brand-primary underline" data-testid="privacy-link">
                      Privacy Policy
                    </Link>
                    {' '}and{' '}
                    <Link to="/refund-policy" target="_blank" rel="noopener noreferrer" className="text-brand-primary underline" data-testid="refund-link">
                      Refund Policy
                    </Link>
                    . I consent to receive booking-related SMS / WhatsApp messages.
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                disabled={loading || !roleKey || !confirmedAge || !agreedToTerms || otpStep !== 'verified'}
                className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-text-secondary">
              Already have an account?{' '}
              <Link to="/login" className="text-brand-primary hover:underline" data-testid="login-link">
                Login here
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
